import { Link } from "react-router-dom";
import {
  MessageCircle, CheckCircle2, ShieldCheck, Layers,
  ArrowRight, MapPin, Stethoscope, Activity, Wind, Radiation,
  FileCheck2, Sparkles, ChevronDown, Award, PackageCheck,
  ClipboardCheck, FileText, Bell, Smartphone,
  Armchair, Syringe, ScanLine, Gauge, Waves, Wrench,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Reveal from "@/components/site/Reveal";
import SillonShowcase from "@/components/site/SillonShowcase";
import ChileCoverage from "@/components/site/ChileCoverage";

const WHATSAPP_NUMBER = "56929894282";
const WA = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
const WHATSAPP_URL = WA("Hola Odontofix, quiero cotizar mantención y agendar visita.");
const EMAIL = "contacto@odontofix.cl";

/* ---------------- data ---------------- */
const pillars = [
  { icon: Award, title: "Técnicos certificados", desc: "Equipo capacitado y en terreno a lo largo de Chile. No improvisamos: diagnóstico, ejecución y pruebas con estándar profesional." },
  { icon: PackageCheck, title: "Repuestos y alternativas", desc: "Stock permanente y alternativas compatibles. No dependes de un solo proveedor ni esperas semanas por una pieza." },
  { icon: Layers, title: "Multiequipo y multimarca", desc: "Dental y médico, todas las marcas. Servicio neutral: no somos el taller cautivo de un importador." },
  { icon: FileCheck2, title: "Trazabilidad total", desc: "App e informes por equipo. Cada intervención queda documentada para tu autorización sanitaria y fiscalización." },
];

const dentalEquip = [
  { icon: Armchair, n: "Sillón dental", d: "Unidad completa: hidráulica, tapiz y motores" },
  { icon: Activity, n: "Autoclave", d: "Mantención y validación de esterilización" },
  { icon: Radiation, n: "Rayos X", d: "Periapical, panorámico y control de calidad" },
  { icon: Gauge, n: "Compresor", d: "Aire comprimido y sistemas de vacío" },
  { icon: Waves, n: "Scaler / Ultrasonido", d: "Transductores, conectores y calibración" },
  { icon: Syringe, n: "Piezas de mano", d: "Turbina, micromotor y jeringa triple" },
];
const medEquip = [
  { icon: Stethoscope, n: "Equipamiento médico general", d: "Diagnóstico y tratamiento" },
  { icon: ShieldCheck, n: "Esterilización clínica", d: "Central de esterilización y DAN" },
  { icon: ScanLine, n: "Imagenología", d: "Equipos de imagen del área médica" },
  { icon: Wind, n: "Aire y vacío medicinal", d: "Redes, compresores y bombas" },
];

const steps = [
  { n: "01", t: "Diagnóstico", d: "Evaluamos el equipo en terreno y detectamos la causa raíz." },
  { n: "02", t: "Plan y presupuesto", d: "Propuesta clara, con repuestos y alternativas." },
  { n: "03", t: "Ejecución", d: "Reparación o preventiva con pruebas de funcionamiento." },
  { n: "04", t: "Informe + seguimiento", d: "Documentación, historial y alertas en la app." },
];

const testimonials = [
  { t: "Ordenamos el historial de todos los equipos y bajamos las urgencias. Ahora sabemos cuándo toca cada mantención.", who: "Red de clínicas", where: "Región Metropolitana" },
  { t: "Responden en regiones con el mismo nivel que en Santiago. Para nosotros eso no tenía precio.", who: "Hospital público", where: "Sur de Chile" },
  { t: "Preventivos al día y trazabilidad impecable para las autorizaciones sanitarias.", who: "Universidad", where: "Zona centro" },
];

/* ---------------- page ---------------- */
export default function Home() {
  return (
    <div data-testid="home-page" className="bg-white">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden odf-hero" data-testid="hero-section">
        <div className="absolute inset-0 odf-hero-gradient" />
        <div className="absolute inset-0 odf-grid opacity-[0.12]" />
        <div className="absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#0FB4B7]/25 blur-[120px]" />
        <div className="absolute bottom-0 -left-24 w-[420px] h-[420px] rounded-full bg-[#0FB4B7]/10 blur-[120px]" />

        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="min-h-[88vh] flex flex-col justify-center py-24 md:py-28">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur px-4 py-1.5 text-xs md:text-sm font-medium text-white mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#0FB4B7]" />
                La red técnica dental y médica más completa de Chile
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[1.05] max-w-4xl">
                Los mejores técnicos<br />
                para tus <span className="odf-text-gradient">equipos clínicos</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed">
                Mantención preventiva y correctiva, repuestos con alternativas e instalación —
                para sillones dentales y equipamiento médico. Cobertura real de norte a sur,
                técnicos certificados y trazabilidad de cada intervención.
              </p>
            </Reveal>
            <Reveal delay={240}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-[#25D366] hover:bg-[#1fb757] text-white h-14 px-8 text-base w-full sm:w-auto shadow-lg shadow-[#25D366]/20" data-testid="hero-whatsapp-btn">
                    <MessageCircle className="h-5 w-5 mr-2" /> Cotizar por WhatsApp
                  </Button>
                </a>
                <Link to="/servicios">
                  <Button size="lg" variant="outline" className="bg-white/5 border-white/30 text-white hover:bg-white hover:text-[#0B1020] h-14 px-8 text-base w-full sm:w-auto backdrop-blur" data-testid="hero-contact-btn">
                    Ver servicios <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </Reveal>
            <Reveal delay={320}>
              <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/70">
                {[
                  { icon: MapPin, t: "Cobertura en las 16 regiones" },
                  { icon: Award, t: "Técnicos certificados" },
                  { icon: PackageCheck, t: "Repuestos y alternativas" },
                  { icon: ShieldCheck, t: "Cumplimiento sanitario" },
                ].map((b) => (
                  <span key={b.t} className="inline-flex items-center gap-2">
                    <b.icon className="h-4 w-4 text-[#0FB4B7]" /> {b.t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center text-white/50 odf-bounce">
          <span className="text-[11px] tracking-widest uppercase mb-1">Baja</span>
          <ChevronDown className="h-5 w-5" />
        </div>
      </section>

      {/* ============ CREDIBILITY BAND ============ */}
      <section className="bg-[#0B1020] border-y border-white/5 py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <p className="text-center text-[11px] uppercase tracking-[0.25em] text-white/40 mb-6">
            Confían en nosotros
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-white/70">
            {["Hospitales públicos", "Universidades", "Redes de clínicas", "Faenas mineras", "Consultas privadas"].map((s) => (
              <span key={s} className="text-sm md:text-base font-semibold text-white/60">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PILLARS ============ */}
      <section className="py-20 md:py-28 bg-white" data-testid="services-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Por qué Odontofix</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111827] leading-tight">
              La red más completa, no un taller más
            </h2>
            <p className="mt-4 text-[#4B5563] text-lg">
              Cuatro razones por las que clínicas y hospitales de todo Chile nos eligen.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <Reveal key={p.title} delay={i * 90}>
                <div className="group h-full rounded-3xl border border-gray-100 bg-[#F8FAFC] p-7 hover:bg-white hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="w-14 h-14 rounded-2xl bg-[#0FB4B7]/10 flex items-center justify-center mb-6 group-hover:bg-[#0FB4B7] transition-colors">
                    <p.icon className="h-7 w-7 text-[#0FB4B7] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#111827] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SILLÓN 360° (fotos reales / scroll-scrub) ============ */}
      <SillonShowcase />

      {/* ============ EQUIPOS QUE MANTENEMOS ============ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Reveal className="max-w-2xl mx-auto text-center mb-14">
            <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Qué mantenemos</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111827] leading-tight">
              Todo tu parque de equipos,<br />un solo proveedor
            </h2>
            <p className="mt-4 text-[#4B5563] text-lg">
              Nuestros técnicos dominan múltiples tecnologías: odontología completa y
              equipamiento del área médica, todas las marcas.
            </p>
          </Reveal>

          <div className="max-w-5xl mx-auto space-y-10">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#0FB4B7]">Odontología</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {dentalEquip.map((e) => (
                  <div key={e.n} className="group flex items-start gap-4 rounded-2xl border border-gray-100 bg-[#F8FAFC] p-5 hover:bg-white hover:shadow-lg hover:border-[#0FB4B7]/30 transition-all duration-300">
                    <div className="w-11 h-11 rounded-xl bg-[#0FB4B7]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0FB4B7] transition-colors">
                      <e.icon className="h-[22px] w-[22px] text-[#0FB4B7] group-hover:text-white transition-colors" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[15px] font-bold text-[#111827] leading-tight">{e.n}</p>
                      <p className="text-[13px] text-[#4B5563] mt-1 leading-snug">{e.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={80}>
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px flex-1 bg-gray-200" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#384091]">Área médica</span>
                <span className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {medEquip.map((e) => (
                  <div key={e.n} className="group flex items-start gap-4 rounded-2xl border border-gray-100 bg-[#F8FAFC] p-5 hover:bg-white hover:shadow-lg hover:border-[#384091]/30 transition-all duration-300">
                    <div className="w-11 h-11 rounded-xl bg-[#384091]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#384091] transition-colors">
                      <e.icon className="h-[22px] w-[22px] text-[#384091] group-hover:text-white transition-colors" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[15px] font-bold text-[#111827] leading-tight">{e.n}</p>
                      <p className="text-[13px] text-[#4B5563] mt-1 leading-snug">{e.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={120}>
              <p className="text-center text-[#4B5563]">
                ¿Tu equipo no está en la lista? Si funciona en tu clínica, lo mantenemos.{" "}
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#0FB4B7] hover:underline">
                  Pregúntanos
                </a>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ COMPLIANCE BAND ============ */}
      <section className="py-20 md:py-24 bg-[#F8FAFC] border-y border-gray-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Reveal className="max-w-3xl mx-auto text-center mb-12">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#384091]/10 text-[#384091] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider mb-5">
              <ShieldCheck className="h-4 w-4" /> Cumplimiento normativo
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111827] leading-tight">
              La mantención no es opcional: es la ley
            </h2>
            <p className="mt-4 text-[#4B5563] text-lg">
              Autoclaves y equipos de rayos X requieren validación y control periódico documentado
              para mantener tu autorización sanitaria. Te dejamos al día y con los certificados en regla.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Activity, t: "Validación de autoclaves", d: "Controles físicos, químicos y biológicos según norma técnica de esterilización del MINSAL." },
              { icon: Radiation, t: "Control de rayos X", d: "Apoyo para autorización, control de calidad y dosimetría exigida por el ISP (DS 133)." },
              { icon: FileCheck2, t: "Respaldo para fiscalización", d: "Informes e historial por equipo, listos para la Seremi de Salud y renovación de autorizaciones." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 90}>
                <div className="h-full rounded-3xl bg-white border border-gray-100 p-7 shadow-sm">
                  <div className="w-12 h-12 rounded-2xl bg-[#384091]/10 flex items-center justify-center mb-5">
                    <c.icon className="h-6 w-6 text-[#384091]" />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-[#111827] mb-2">{c.t}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ COVERAGE ============ */}
      <ChileCoverage />

      {/* ============ PROCESS ============ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Cómo trabajamos</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111827]">Simple, ordenado y trazable</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.n} delay={i * 90}>
                <div className="relative h-full rounded-3xl border border-gray-100 bg-[#F8FAFC] p-7">
                  <span className="font-heading text-5xl font-extrabold text-[#0FB4B7]/20">{s.n}</span>
                  <h3 className="font-heading text-lg font-bold text-[#111827] mt-2 mb-2">{s.t}</h3>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{s.d}</p>
                  {i < steps.length - 1 && (
                    <ArrowRight className="hidden md:block absolute top-8 -right-3 h-6 w-6 text-gray-200" />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ APP / TRACEABILITY ============ */}
      <section className="py-20 md:py-28 bg-[#0B1020] relative overflow-hidden" data-testid="app-section">
        <div className="absolute -top-20 right-1/4 w-[400px] h-[400px] rounded-full bg-[#0FB4B7]/15 blur-[120px]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <span className="inline-block bg-[#0FB4B7] text-white px-4 py-1 rounded-full text-sm font-semibold mb-5">Gratis para clientes</span>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight mb-5">
                Cada intervención,<br />documentada en tu bolsillo
              </h2>
              <p className="text-white/65 text-lg leading-relaxed mb-8">
                La app Odontofix centraliza tus equipos, su historial de mantenciones, informes y
                alertas preventivas. Trazabilidad completa para tu operación y tus autorizaciones.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8 max-w-md">
                {[
                  { icon: ClipboardCheck, t: "Registro de equipos" },
                  { icon: FileText, t: "Historial e informes" },
                  { icon: Bell, t: "Alertas preventivas" },
                  { icon: Smartphone, t: "Acceso desde cualquier lugar" },
                ].map((f) => (
                  <div key={f.t} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="h-5 w-5 text-[#0FB4B7]" />
                    </div>
                    <span className="text-sm font-medium text-white/85">{f.t}</span>
                  </div>
                ))}
              </div>
              <Link to="/app">
                <Button className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white h-12 px-6">
                  Conocer la app <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </Reveal>
            <Reveal delay={120}>
              <div className="flex justify-center">
                {/* mockup self-contained */}
                <div className="w-[260px] rounded-[2.5rem] bg-gray-900 p-3 shadow-2xl border border-white/10">
                  <div className="rounded-[2rem] overflow-hidden bg-[#F5F7FA]">
                    <div className="bg-[#384091] px-5 py-3 flex items-center justify-between">
                      <span className="text-white text-xs font-medium">9:41</span>
                      <span className="text-white/90 text-xs font-semibold">Odontofix</span>
                      <span className="w-5 h-2.5 rounded-sm bg-white/70" />
                    </div>
                    <div className="p-4 space-y-3 min-h-[380px]">
                      <p className="text-[11px] text-gray-400 font-medium">Mis equipos</p>
                      {[
                        { n: "Sillón dental · Box 6", s: "Al día", c: "#059669" },
                        { n: "Autoclave clase B", s: "Validación en 20 días", c: "#D97706" },
                        { n: "Rayos X panorámico", s: "Al día", c: "#059669" },
                        { n: "Compresor central", s: "MP programada", c: "#0FB4B7" },
                      ].map((e) => (
                        <div key={e.n} className="rounded-xl bg-white p-3 shadow-sm flex items-center justify-between">
                          <div>
                            <p className="text-[11px] font-semibold text-gray-800 leading-tight">{e.n}</p>
                            <p className="text-[10px] mt-0.5" style={{ color: e.c }}>{e.s}</p>
                          </div>
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: e.c }} />
                        </div>
                      ))}
                      <div className="rounded-xl bg-[#0FB4B7] p-3 text-center">
                        <p className="text-[11px] font-semibold text-white">Solicitar visita técnica</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="py-20 md:py-28 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs tracking-[0.3em] font-semibold text-[#0FB4B7] uppercase mb-4">Resultados</p>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-[#111827]">Lo que logran nuestros clientes</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="h-full rounded-3xl border border-gray-100 bg-[#F8FAFC] p-8">
                  <div className="text-[#0FB4B7] text-5xl font-heading leading-none mb-3">&ldquo;</div>
                  <p className="text-[#1F2937] leading-relaxed mb-6">{t.t}</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
                    <div className="w-10 h-10 rounded-full bg-[#0FB4B7]/10 flex items-center justify-center">
                      <CheckCircle2 className="h-5 w-5 text-[#0FB4B7]" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#111827]">{t.who}</p>
                      <p className="text-xs text-[#4B5563]">{t.where}</p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="relative overflow-hidden py-24 md:py-32 bg-[#384091]" data-testid="final-cta-section">
        <div className="absolute inset-0 odf-grid opacity-10" />
        <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full bg-[#0FB4B7]/25 blur-[140px]" />
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight max-w-3xl mx-auto">
              Tu equipo detenido cuesta.<br />Nosotros lo mantenemos andando.
            </h2>
            <p className="mt-6 text-xl text-white/75 max-w-2xl mx-auto">
              Cotiza mantención para tus equipos clínicos. Respuesta rápida, sin compromiso.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="bg-[#25D366] hover:bg-[#1fb757] text-white h-14 px-8 text-base w-full sm:w-auto shadow-lg" data-testid="final-cta-whatsapp">
                  <MessageCircle className="h-5 w-5 mr-2" /> Hablar por WhatsApp
                </Button>
              </a>
              <a href={`mailto:${EMAIL}`}>
                <Button size="lg" variant="outline" className="bg-white/5 border-white/40 text-white hover:bg-white hover:text-[#384091] h-14 px-8 text-base w-full sm:w-auto backdrop-blur">
                  Enviar correo
                </Button>
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
